/* eslint-disable no-nested-ternary */
/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2024 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 */

import React, { Fragment, Suspense } from 'react';
import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import xss from 'xss';
import {
  ASSET_TABS_VALUE,
  AUTH_HOME,
  MLM_AUTH_HOME,
  CHANNEL_QUERY_PARAM,
  CHANNEL_ASSET_QUERY_PARAM,
  CHANNEL_ASSET_PRATICE_QUERY_PARAM,
  CHANNEL_CHANNEL_QUERY_PARAM,
  CHANNEL_SECTION_QUERY_PARAM,
  CHANNEL_GET_UNSTUCK,
  ACTIVE,
  ACTIVITY_TYPE,
  BUNDLE,
  CHANNEL_LINKS,
  CONTENT_TYPE_CITE,
  CONTENT_TYPE_EPUB,
  ETEXT_PDF,
  EVENT_AUTHHOMEEVENT,
  GO_TO_PAGE_MAX_LENGTH,
  HERO_BANNER_TYPES,
  HERO_CONTENT_CARD_POSITIONS,
  HERO_ICONS,
  HERO_STYLES,
  LIBRARY_EVENT,
  LOCATION_IN_APP,
  PPLUS_SUBSCRIPTIONS,
  READ,
  SET_HERO_DASHBOARD,
  TOC_SEPERATOR_CHAPTER,
  ASSET_FETCHED,
  ASSET_FAILED,
  CHANNEL_TOC_FAILED,
  CHANNEL_TOC_FETCHED,
  CHANNEL_TOC_REQUESTED,
  AUTHHOME_APPLICATION,
  COURSE_TAB_EVENT,
  MONDLY_CARD_POSITIONS,
  EVENT_USER_INTERACTION,
  AUTH_HOME_CATEGORY,
  CHANNEL_CARD_EVENT,
  DEFAULT_TEMPLATE,
  ONBOARDING_HEI_AB_TESTING_EVENT,
  HEADER_EVENT,
  COURSE_COLLECTION_SERVICE_REQUESTED,
  HTTP_REQUEST_METHODS,
  EXAM_PREP_FLOW,
  AI_STUDY_TOOL_EVENT,
  CHANNEL_MAPPING_TYPES,
  ETEXT_RELATED_CHANNEL,
  STUDY_EXAM_COMPONENT_POSITION,
  ETEXT_MAPPEDCHANNEL_LITE,
  AI_CHAT_VARIANT,
  VARIANT_V1,
  LD_EVENT_NAME,
  STUDY_EXAM_PREP_SUBSCRIPTION_STATUS_PARAM,
  CHANNELS_CONTAINER_PARAM,
  VARIANT_B,
  CHANNEL_HYPER_LINKS,
  PRACTICE_ASSET_VARIANT,
  LEFT_NAV_MENU_KEY,
  GLOBAL_NAV_EVENT,
  APPSTORE_RATING,
  PLAYSTORE_RATING,
  QR_CODE_VARIANT,
  QR_CODE_TEST_VARIANT_EVENT_LABEL,
  EREADER_CATEGORY,
  EVENT_EREADEREVENT,
  CHANNEL_OPTION_CB_VALUES,
  INIT_WITH_EXAMS,
  EXAM_REVIEW_COMPLETE,
  THEMES,
  HOME_INSTRUCTOR_COURSE_ROUTES
} from '../../../common/constants';
import AETHomePlugin from '../../../common/plugins/AETHomePlugin';
import ProductUtils from '../../../common/utils/ProductUtils';
import CommonUtils from '../../../common/utils/CommonUtils';
import env from '../../../common/env';
import LoadingContainer from '../../../common/components/LoadingContainer';
import ChannelExamsContainer from '../ChannelExamsContainer';
import { HeroGenericShimmer, ChannelsSliderLoader } from '../Loader/ComponentLoader';
import Container from '../../../common/components/Container';

const PopularChannelsSlider = CommonUtils.lazyWithPreload(() => import('@authhome/library')
  .then(module => ({ default: module.PopularChannelsSlider })));

const HeroDashboard = CommonUtils.lazyWithPreload(() => import('@authhome/library')
  .then(module => ({ default: module.HeroDashboard })));

const QrCodeContainer = CommonUtils.lazyWithPreload(() => import('@authhome/library')
  .then(module => ({ default: module.QrCodeContainer })));

export default class HeroDashboardPlugin {
  constructor(context, location = AUTH_HOME) {
    this.context = context;
    this.userLastVisitedId = '';
    this.userChannelRecentTopic = {};
    this.isInitialLoad = false;
    this.init();
    this.location = location;
    this.isTocClicked = false;
    this.AIStudyToolIsInputDirty = false;
  }

  static activeCourseTab = null;

  static isBundleUser = false;

  static hasChannelSubscription = false;

  init() {
    Framework.getEventManager().on(SET_HERO_DASHBOARD, props => this.setHeroDashboard(props));
    Framework.getEventManager().on(ASSET_FETCHED, () => this.setTableOfContentData());
    Framework.getEventManager().on(ASSET_FAILED, () => this.setTableOfContentError());
    Framework.getEventManager().on(CHANNEL_TOC_FETCHED, (response) => {
      this.setChannelTOCData(response);
    });
    Framework.getEventManager().on(CHANNEL_TOC_FAILED, () => this.setTableOfContentError());
  }

  renderDiagnosticsInfo = () => {
    const { bookshelf = {}, user, language } = this.context.props;
    let mappedDiagnosticsInfo = {};
    const books = getSnapshot(bookshelf?.books || []);
    const userInfo = getSnapshot(user || {});
    const isStudent = CommonUtils.getUserRole(books);
    const bookWithDiagnosticsInfo = books.find(book => book?.diagnosticsInfo);
    if (!bookWithDiagnosticsInfo || !bookWithDiagnosticsInfo.diagnosticsInfo) {
      return null;
    }
    if (bookWithDiagnosticsInfo.diagnosticsInfo.isError) {
      mappedDiagnosticsInfo = { ...bookWithDiagnosticsInfo.diagnosticsInfo, diagnosticsErrorMsg: language.getMessage('diagnostics.errorMsg') };
    } else {
      mappedDiagnosticsInfo = CommonUtils.mapDiagnosticsInfo(
        bookWithDiagnosticsInfo.diagnosticsInfo,
        userInfo,
        bookWithDiagnosticsInfo.course_id,
        bookWithDiagnosticsInfo.product_model,
        isStudent
      );
    }
    return mappedDiagnosticsInfo || null;
  };

  /**
   * Get the title info
   * @returns {Object} - The title info props
   * @description This method is used to get the title info props for the hero dashboard
   */
  getTitleInfo = () => {
    const { herobanner } = this.context.props;
    const { titleInfo } = getSnapshot(herobanner);
    const productModelName = this.getProductModelName(titleInfo);
    const renderTitleInfo = { ...titleInfo, productModelName };

    return renderTitleInfo;
  };

  /**
   * Renders the AET dashboard
   * @param {Boolean} showBody - true/false
   * @returns {React.ReactNode}
   */
  renderAETDashboard = (showBody = true) => {
    const { classes = {} } = this.context.props;
    const { instructorDashboardUtils } = this.context;

    return (
      <div className={classes.root}>
        <div
          className={`${classes.content} Hero-dashboard-section-container`}
          data-title={LOCATION_IN_APP.COURSE_TAB}
        >
          {instructorDashboardUtils.renderCourseHeader()}
          {showBody && instructorDashboardUtils.renderTabs()}
          {instructorDashboardUtils.renderViewCourseIdModal()}
        </div>
      </div>
    );
  };

  renderHeroDashboard = (isScreenResBelow1024 = false) => {
    const {
      herobanner,
      user,
      language,
      classes
    } = this.context.props;
    const { flags } = this.context.state;
    const {
      template,
      courseId,
      altCourseId
    } = HeroDashboardPlugin.activeCourseTab || {};
    const {
      bannerType,
      channelId,
      heroHeading,
      heroSubHeading,
      assets,
      subscriptionStatus,
      tableOfContents,
      titleInfo,
      goToPage,
      heroContentCard,
      isAETEnabled
    } = getSnapshot(herobanner);

    const config = this.getConfig(bannerType, { channelId, isScreenResBelow1024 });
    const promotionalLinksData = herobanner.getPromotionalLinks(channelId);
    const promotionalLinks = (promotionalLinksData && promotionalLinksData.length > 1) ? {
      title: this.getStudyPrepOrPackText(),
      listContent: promotionalLinksData
    } : null;
    HeroDashboardPlugin.isBundleUser = CommonUtils.hasSubscription(user, [ACTIVE], [BUNDLE]);
    HeroDashboardPlugin.hasChannelSubscription = CommonUtils.isChannelsSubscriber();
    this.setSubscriptionStatusData({
      subscriptionStatus,
      config
    });
    this.setNotificationData(titleInfo);
    const productModelName = this.getProductModelName(titleInfo);
    const renderTitleInfo = { ...titleInfo, productModelName };
    const renderDiagnosticsInfo = this.renderDiagnosticsInfo();
    const studyExamPrepCardPosition = this.context.getContainerData(
      'heroView', STUDY_EXAM_COMPONENT_POSITION, HERO_CONTENT_CARD_POSITIONS.BOTTOM
    );

    if (isAETEnabled) {
      return (
        this.renderAETDashboard()
      );
    }

    if (herobanner.config?.isNonMlmAeTHeroContext) {
      return (
        this.renderAETDashboard(false)
      );
    }

    const isHome2 = this.location === MLM_AUTH_HOME;
    const isUserBookPreferenceFetched = isHome2 ? herobanner.isUserBookPreferenceFetched : true;
    const isReadyToRender = !!bannerType && isUserBookPreferenceFetched;

    return (
      <Suspense fallback={<HeroGenericShimmer withoutWrapper />}>
        <LoadingContainer isReadyToRender={isReadyToRender} loader={<HeroGenericShimmer withoutWrapper />}>
          <HeroDashboard
            className={`Hero-dashboard-section-container ${classes.heroDashboard}`}
            config={config}
            data={{
              bannerType,
              heroHeading,
              heroSubHeading,
              assets,
              styles: this.getHeroStyles(template),
              promotionalCard: herobanner.getPromotionalCardData(),
              subscriptionStatus: {
                ...subscriptionStatus,
                cta: this.getSubscriptionStatusCardCta(bannerType)
              },
              heroHeader: {
                title: this.getHeroTopicContainerHeading(),
                cta: this.getHeroHeaderCTA(),
                description: this.getHeroDescription(),
                isSubscribed: CommonUtils.isChannelsSubscriber(),
                subscriptionText: this.getSubscriptionText()
              },
              heroFooter: this.getHeroFooter(),
              tableOfContents,
              heroContentCard: {
                ...heroContentCard,
                callForAction: {
                  ...(heroContentCard?.callForAction || {}),
                  variant: this.variantOfHeroContentCardCTA((heroContentCard?.callForAction || {}), config)
                },
                goToPage,
                titleInfo: renderTitleInfo,
                diagnosticsInfo: renderDiagnosticsInfo
              },
              popularChannels: this.getPopularChannelList(herobanner.getPopularChannels()),
              marketingCard: this.getMarketingCardData(),
              promoCard: this.getPromoCardData(bannerType === HERO_BANNER_TYPES.MARKETING_TEMPLATE),
              highlightPromoCard: this.getHighlightPromoCard(),
              promotionalLinks,
              aiChatInputData: this.getAiInputData(config),
              relatedChannelCard: this.getRelatedChannelCardData(),
              studyExamPrepCardPosition,
              assetTabs: this.getAssetTabs(),
              qrCode: this.getQrCodeData(),
              schoolInfo: this.context.schoolInfoPlugin.getSchoolInfoData()
            }}
            callbacks={{
              onHeroHeaderCTAClick: this.onHeroHeaderCTAClick,
              handleTocClick: this.onTocClick,
              handleChapterIdChange: this.handleChapterIdChange,
              onAssetCardClick: this.onAssetCardClick,
              handleHeroThumbnailClick: this.handleHeroThumbnailClick,
              handleCtaClick: this.handleCtaClick,
              handleIconsClick: this.handleIconsClick,
              onTitleInfoBookCTAClick: this.onTitleInfoBookCTAClick,
              onTitleInfoNotificationCTAClick: this.onTitleInfoNotificationCTAClick,
              onTitleInfoRemoveClick: this.onTitleInfoBookCTAClick,
              onGoToPageNoChange: this.onGoToPageNoChange,
              onGoToPageInputBlur: this.onGoToPageInputBlur,
              onPromotionalLinksClick: this.onPromotionalLinksClick,
              onSubStatusCardLinksClick: this.onSubStatusCardLinksClick,
              onRemoveCourse: this.onRemoveCourse,
              onPopularChannelCardClick: this.onPopularChannelCardClick,
              onHEIStartLearningClick: this.onHEIStartLearningClick,
              onHighlightPromoClick: this.onHighlightPromoClick,
              onPromotionalCardCTAClick: this.onPromotionalCardCTAClick,
              onSubStatusCardCTAClick: this.onSubStatusCardCTAClick,
              onAIChatbotSend: this.onAIChatbotSend,
              onAIInputChange: this.onAIInputChange,
              onRelatedChannelClick: this.onRelatedChannelClick,
              onSeeAllClick: () => this.context.onSeeAll(false),
              onPracticeCTAClick: this.onPracticeCTAClick,
              onAssetTabClick: this.onAssetTabTabClick,
              onLaunchDiagnosticsClick: this.onLaunchDiagnosticsClick,
              onQrCodeCloseClick: this.handleQrCodeClose,
              onQrCodeInit: this.onQrCodeInit
            }}
            components={{
              ExamPrepComponent: this.renderExamPrepComponent(user, channelId, courseId, altCourseId),
              PopularChannelSlider: this.renderPopularChannelSlider()
            }}
            onInit={this.context.initPageTracker}
            locale={language.currentLanguage}
          />
        </LoadingContainer>
      </Suspense>
    );
  };

  /**
   * Handle highlight promo card CTA click
   *
   */
  onHighlightPromoClick = (startLearningCTAUrl) => {
    const { herobanner, featureFlagsClient } = this.context.props;
    const { flags } = this.context.state;
    featureFlagsClient.track(LD_EVENT_NAME.HERO_HIGHLIGHT_PROMO_CARD_CLICK);

    if (this.context.commonPlugin.isIntegratedUI()) {
      this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, startLearningCTAUrl);
    } else {
      window.open(startLearningCTAUrl);
    }

    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      channel_id: herobanner.channelId || null,
      container_value: CommonUtils.getCurrentScreenEventLabel(flags),
      location_in_app: LOCATION_IN_APP.AI_TUTOR_CARD
    };
    // Event 581
    CommonUtils.dispatchGaEvent(
      AUTH_HOME_CATEGORY,
      EVENT_AUTHHOMEEVENT,
      CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_CLICK_EVENT_ACTION,
      CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_EVENT_LABEL,
      customFields
    );
  };

  renderPopularChannelSlider = () => {
    const { herobanner, language } = this.context.props;
    const heroBook = herobanner.getHeroBook() || {};
    const isStudent = CommonUtils.getIsStudent(heroBook);
    const isAETEnabled = herobanner.isAETEnabled;
    const isNonMlmAeTHeroContext = herobanner.config?.isNonMlmAeTHeroContext;
    const isChannelSubscriber = CommonUtils.isChannelsSubscriber();

    // Hide popular channels for AET or non-MLM AET hero context when user is instructor or non-subscriber student
    if (
      (isAETEnabled || isNonMlmAeTHeroContext)
      && (!isStudent || (isStudent && !isChannelSubscriber))
    ) {
      return null;
    }
    const isBundleUser = HeroDashboardPlugin.isBundleUser;
    const popularChannelsStore = getSnapshot(herobanner.popularChannels);
    const isLoaded = (popularChannelsStore?.channelCards?.length > 0) && !!herobanner.bannerType;
    const { currentPopularChannelFilter } = this.context.state;

    return (
      <LoadingContainer
        isReadyToRender={isLoaded}
        loader={<ChannelsSliderLoader />}
      >
        <Suspense fallback={<ChannelsSliderLoader />}>
          <PopularChannelsSlider
            channelCards={this.context.commonPlugin.getSortedPopularChannels(herobanner.popularChannels?.channelCards, this.context.state.currentPopularChannelFilter)}
            headingText={CommonUtils.getChannelContainerCopytext(herobanner.popularChannels?.channelCards, isBundleUser)}
            onPopularChannelCardClick={(index) => {
              if (this.location === MLM_AUTH_HOME) {
                this.onMLMPopularChannelCardClick(index);
                return;
              }
              this.context.coursePlugin.handlePopularChannelCardClick(index);
              window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth'
              });
            }}
            onPopularChannelFilterItemClick={(title) => {
              this.context.commonPlugin.setPopulatChannelFilter(title);
            }}
            gap={18}
            filterList={this.context.commonPlugin.getPopularChannelsFilterData(herobanner.popularChannels?.channelCards, currentPopularChannelFilter)}
            isSubscribed={CommonUtils.isChannelsSubscriber()}
            subscriptionText={this.getSubscriptionText()}
            config={this.context.commonPlugin.getPopularChannelsConfig()}
            locale={language.currentLanguage}
            className="container-default"
            theme={THEMES.LIGHT}
          />
        </Suspense>
      </LoadingContainer>
    );
  };

  /**
   * method to smooth scroll
   *
   */
  examContainerOnComplete = () => {
    // Force re-render to fetch latest data
    this.context.setState({ channelExamsRefreshKey: this.context.state.channelExamsRefreshKey + 1 });

    const examsPeriod = this.context.getContainerData(
      'heroView',
      STUDY_EXAM_COMPONENT_POSITION,
      HERO_CONTENT_CARD_POSITIONS.BOTTOM
    ) === HERO_CONTENT_CARD_POSITIONS.TOP;
    if (examsPeriod) {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  /**
   * If newUI locationInApp will be examprep_card else examprep_container
   *
   */
  examContainerOnGoToChannels = (redirectUrl, event, options) => {
    if (this.context.commonPlugin.isIntegratedUI()) {
      event.preventDefault();
    }
    const locationInApp = LOCATION_IN_APP.TAILORED_EXAM_CARD;
    if (CommonUtils.localStringCompare(options, CHANNEL_OPTION_CB_VALUES.USER)) {
      this.tiggerChannelCardElement(locationInApp);
    }
    this.handleCourseTabEvents();
    if (this.context.commonPlugin.isIntegratedUI()) {
      this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, redirectUrl);
    }
  };

  /**
   * Method to check exams uploaded or not
   *
   */
  // eslint-disable-next-line class-methods-use-this
  examContainerOnStateChange = (uploadState) => {
    const hasExamsOnLoad = CommonUtils.localStringCompare(uploadState, INIT_WITH_EXAMS);
    const hasUploadedExams = CommonUtils.localStringCompare(uploadState, EXAM_REVIEW_COMPLETE);
    const hasExams = hasExamsOnLoad || hasUploadedExams;

    this.context.setState({ hasExams });
  };

  renderExamPrepComponent = (user, channelId, courseId, altCourseId) => {
    const { language } = this.context.props || {};
    const { flags } = this.context.state;
    const courseIds = [];
    const examsPeriod = this.context.getContainerData(
      'heroView',
      STUDY_EXAM_COMPONENT_POSITION,
      HERO_CONTENT_CARD_POSITIONS.BOTTOM
    ) === HERO_CONTENT_CARD_POSITIONS.TOP;

    if (courseId) courseIds.push(courseId);
    if (altCourseId) courseIds.push(altCourseId);
    const { id: userId } = getSnapshot(user);
    const { channelExamsRefreshKey = 0 } = this.context.state;

    return (
      <ChannelExamsContainer
        key={channelExamsRefreshKey}
        userId={userId}
        env={env.ENVIRONMENT}
        channelId={channelId}
        locale={language.currentLanguage}
        onGoToChannels={this.examContainerOnGoToChannels}
        locationInApp={EXAM_PREP_FLOW.EXAM_PREP_CONTAINER}
        isBundleUser={HeroDashboardPlugin.isBundleUser}
        mlmCourseIds={courseIds}
        examsPeriod={examsPeriod}
        onComplete={this.examContainerOnComplete}
        newUI={false}
        showSampleExams={flags.enableSampleExams}
        onStateChange={this.examContainerOnStateChange}
      />
    );
  };

  /**
   * Resets the local state for instructor course dashboard
   */
  setResets = () => {
    this.context.instructorDashboardUtils.resetLocalState();
    // Reset checkbox state bulk delete
    this.context.setState({ showCheckbox: false });
    // Reset assignments fetched state
    this.context.setState({ isAssignmentsFetched: false });
    const { assignment } = this.context.props;
    assignment.resetAssignments();
    const { channelRemediationByBook } = this.context.props;
    channelRemediationByBook.reset();
    const { studentsRoster } = this.context.props;
    studentsRoster.resetStudentsRoster();
    const { students } = this.context.props;
    students.resetStudents();
  };

  setHeroDashboard({
    fetchChannels = false,
    courseList,
    isInitialLoad = false
  }) {
    this.userChannelRecentTopic = {};
    this.isTocClicked = false;

    this.setActiveCourseTab(courseList);
    const { template = HERO_BANNER_TYPES.MARKETING_TEMPLATE } = HeroDashboardPlugin.activeCourseTab || {};
    const { herobanner } = this.context.props;
    herobanner.reset();
    const heroBannerStore = getSnapshot(herobanner);
    let heroStoreData = { ...heroBannerStore };
    heroStoreData.config = this.getConfig(template, { channelId: heroStoreData.channelId });
    heroStoreData = { ...heroStoreData, ...this.constructHeroData(template, heroStoreData?.config) };

    herobanner.setHeroBanner(
      heroStoreData,
      fetchChannels && heroStoreData.bannerType === HERO_BANNER_TYPES.BOOK_WITH_CHANNEL
    );
    this.context.pendoUtils.initializePendoTriggers();
    herobanner.fetchUserBookPreference();
    herobanner.fetchChannelDetails();
    this.isInitialLoad = isInitialLoad;

    // Fetch instructor data if AET is enabled
    if (heroStoreData?.isAETEnabled) {
      const heroBook = this.getHeroBook();
      this.fetchAsset(heroBook);
      this.setResets();
      this.context.instructorDashboardUtils.handleOnHeroBannerSet();
    }

    // Reset exams state
    this.context.setState({ hasExams: null });

    if (!isInitialLoad || !heroStoreData?.isAETEnabled) {
      // Redirect to home if not initial load
      const { match } = this.context.props;
      Framework.redirectTo(CommonUtils.getContextAwareRoute('HOME', match));
    }

    // Update Pendo metadata on non-AET enabled hero
    if (!heroStoreData?.isAETEnabled) {
      this.context.commonPlugin.InitializePendo(true, {});
    }

    this.tiggerABTestingEvent(heroStoreData.bannerType, isInitialLoad);
  }

  setMLMHeroDashboard() {
    this.userChannelRecentTopic = {};
    const { template = HERO_BANNER_TYPES.MARKETING_TEMPLATE } = HeroDashboardPlugin.activeCourseTab || {};
    const { herobanner } = this.context.props;
    herobanner.reset();
    const heroBannerStore = getSnapshot(herobanner);
    let heroStoreData = { ...heroBannerStore };
    heroStoreData.config = this.getConfig(template, { channelId: heroStoreData.channelId, showPopularChannels: false });
    heroStoreData = { ...heroStoreData, ...this.constructHeroData(template, heroStoreData?.config) };
    this.context.authhomeAIStudyToolPlugin.fetchHeroBookAsset();

    herobanner.setHeroBanner(
      heroStoreData,
      heroStoreData.bannerType === HERO_BANNER_TYPES.BOOK_WITH_CHANNEL
    );
    this.context.pendoUtils.initializePendoTriggers();

    herobanner.fetchUserBookPreference();

    // Fetch instructor data if AET is enabled
    if (heroStoreData?.isAETEnabled) {
      this.context.instructorDashboardUtils.handleOnHeroBannerSet();
    } else if (heroStoreData?.config?.isNonMlmAeTHeroContext) {
      this.context.instructorDashboardUtils.fetchInstructorData();
    }

    herobanner.fetchChannelDetails();
  }

  /**
   * Sets hero dashboard when a course is selected from the dropdown (home + home2).
   * Combines Auth Home and MLM hero dashboard behaviour for course switching.
   *
   * @param {boolean} isInitialLoad - Initial load skips redirect when AET is enabled
   */
  setActiveDashboard(isInitialLoad) {
    const isMLMRoute = this.location === MLM_AUTH_HOME;

    this.userChannelRecentTopic = {};
    this.isTocClicked = false;

    const { template = HERO_BANNER_TYPES.MARKETING_TEMPLATE } = HeroDashboardPlugin.activeCourseTab || {};
    const { herobanner } = this.context.props;

    herobanner.reset();
    const heroBannerStore = getSnapshot(herobanner);
    let heroStoreData = { ...heroBannerStore };
    const configOptions = isMLMRoute
      ? { channelId: heroStoreData.channelId, showPopularChannels: false }
      : { channelId: heroStoreData.channelId };

    heroStoreData.config = this.getConfig(template, configOptions);
    heroStoreData = { ...heroStoreData, ...this.constructHeroData(template, heroStoreData?.config) };

    if (isMLMRoute) {
      // Fetch TOC for AI study tool
      this.context.authhomeAIStudyToolPlugin.fetchHeroBookAsset();
    }

    const shouldFetchChannels = heroStoreData.bannerType === HERO_BANNER_TYPES.BOOK_WITH_CHANNEL;

    herobanner.setHeroBanner(heroStoreData, shouldFetchChannels);
    this.context.pendoUtils.initializePendoTriggers();
    herobanner.fetchUserBookPreference();

    if (heroStoreData?.isAETEnabled) {
      if (!isMLMRoute) {
        const heroBook = this.getHeroBook();
        this.fetchAsset(heroBook);
      }

      this.setResets();
      this.context.instructorDashboardUtils.handleOnHeroBannerSet();
    } else if (isMLMRoute && heroStoreData?.config?.isNonMlmAeTHeroContext) {
      this.context.instructorDashboardUtils.fetchInstructorData();
    }

    this.context.setState({ hasExams: null });

    const isInstructorCourseRoutes = window?.location?.pathname?.includes('/course');

    if ((!isInitialLoad || !heroStoreData?.isAETEnabled) && !isInstructorCourseRoutes) {
      const { match } = this.context.props;
      Framework.redirectTo(CommonUtils.getContextAwareRoute('HOME', match));
    }

    if (!heroStoreData?.isAETEnabled) {
      this.context.commonPlugin.InitializePendo(true, {});
    }

    if (!isMLMRoute) {
      this.tiggerABTestingEvent(heroStoreData.bannerType, isInitialLoad);
    }

    herobanner.fetchChannelDetails();
    this.isInitialLoad = isInitialLoad;
  }

  constructHeroData = (template, config = {}) => {
    let data = {};
    const {
      BOOK,
      BOOK_WITH_CHANNEL,
      CHANNEL,
      MARKETING_TEMPLATE
    } = HERO_BANNER_TYPES;

    if (template === BOOK) {
      data = this.getEtextOnlyData(config);
    } else if (template === BOOK_WITH_CHANNEL) {
      data = this.getBookWithChannelData(config);
    } else if (template === CHANNEL) {
      data = this.getChannelOnlyData(config);
    } else {
      data = {
        bannerType: MARKETING_TEMPLATE
      };
    }

    return data;
  };

  setActiveCourseTab = (courseList) => {
    const { coursecollection } = this.context.props || {};
    let activeCourseTab = null;

    if (courseList && courseList?.length > 0) {
      const activeCourse = courseList.find(course => course.active);
      let courseTabProps = {};

      if (activeCourse && activeCourse.collection && activeCourse.collection.length > 0) {
        coursecollection.setActiveCourse(activeCourse);
        courseTabProps = { ...activeCourse.collection[0] };
        courseTabProps.isMLMcourse = activeCourse.isMLMcourse;
      }
      activeCourseTab = courseTabProps;
    }

    HeroDashboardPlugin.activeCourseTab = activeCourseTab;
  };

  constructBookLinks = () => {
    const { language } = this.context.props;

    return [
      {
        icon: HERO_ICONS.TOC_ICON,
        text: language.getMessage('hero.tableOfContents')
      },
      {
        icon: HERO_ICONS.SEARCH_ICON,
        text: language.getMessage('hero.search')
      },
      {
        icon: HERO_ICONS.FLASHCARD_ICON,
        text: language.getMessage('hero.flashcards')
      }
    ];
  };

  /**
   * To decide a hero content card CTA varaint in case of highlightPromoCard present at top
   *
   * @param {*} param
   * @param {*} config
   * @returns
   */
  // eslint-disable-next-line class-methods-use-this
  variantOfHeroContentCardCTA = ({ isPrimary = false }, config) => {
    let variant = isPrimary ? 'primary' : 'secondary';
    const {
      componentsToRender,
      highlightPromoCard
    } = config || {};

    if (componentsToRender?.showHighlightPromoCard && highlightPromoCard?.position === HERO_CONTENT_CARD_POSITIONS.TOP) {
      variant = 'secondaryFilled';
    }

    return variant;
  };

  constructBookData = ({
    heroBook = {},
    config = {},
    isEtextOnly = false
  }) => {
    const { language } = this.context.props;
    const {
      heroContentCard: heroContentCardConfig,
      componentsToRender
    } = config || {};
    const heroContentCard = {};
    heroContentCard.variant = DEFAULT_TEMPLATE;
    heroContentCard.heading = language.getMessage('hero.yourETextbook');
    heroContentCard.title = heroBook.title;
    heroContentCard.thumbnailImage = heroBook.cover_image_url;
    heroContentCard.titleInfo = {
      author: heroBook.author,
      bookTitle: heroBook.title,
      courseName: this.getCourse(heroBook.courses).course_name,
      coverImageUrl: heroBook.cover_image_url,
      isbn: heroBook.isbn,
      productModelName: heroBook.product_model_name,
      book_id: heroBook.book_id,
      product_id: heroBook.product_id,
      course_id: heroBook.course_id,
      courseId: heroBook.course_id,
      notificationsData: heroBook.bannerData || null,
      showRemoveEntitlement: heroBook.showRemoveEntitlement
    };
    if (heroContentCardConfig?.showGoToPage) {
      heroContentCard.goToPage = {
        placeholder: '',
        label: language.getMessage('hero.goToPage.label'),
        value: heroBook.last_accessed_print_page_number,
        initialValue: heroBook.last_accessed_print_page_number,
        maxLength: GO_TO_PAGE_MAX_LENGTH
      };
    }
    if (componentsToRender?.showToc) {
      heroContentCard.tableOfContents = {
        chapterTitle: heroBook.last_location_read || language.getMessage('hero.toc.defaultPlacehoder'),
        chapterId: heroBook.last_location_read_id || '',
        initialChapterId: heroBook.last_location_read_id || ''
      };
    }
    if (heroContentCardConfig?.showCallForAction) {
      heroContentCard.callForAction = {
        text: language.getMessage('hero.openEtextBookV1'),
        ariaLabel: language.getMessage('hero.openEtextBookV1'),
        isPrimary: isEtextOnly
      };
    }
    if (heroContentCardConfig.showLinks) {
      heroContentCard.links = this.constructBookLinks();
      heroContentCard.eTextFeaturesCopy = {
        title: language.getMessage('hero.etextfeaturestitle'),
        description: language.getMessage('hero.etextfeaturesdescription')
      };
    }
    if (heroContentCardConfig.showMyEtextbookLink) {
      heroContentCard.myEtextbookLinkData = {
        text: language.getMessage('hero.myetextbook.text'),
        linkText: language.getMessage('hero.myetextbook.link')
      };
    }

    return heroContentCard;
  };

  constructChannelOnlyData = ({
    heroChannel = {},
    config = {}
  }) => {
    const { language } = this.context.props;
    const {
      heroContentCard: heroContentCardConfig
    } = config || {};
    const {
      title,
      thumbnailUrl
    } = heroChannel || {};
    const heroContentCard = {};
    heroContentCard.heading = language.getMessage('hero.startStudying');
    heroContentCard.title = title;
    heroContentCard.thumbnailImage = thumbnailUrl;
    heroContentCard.variant = DEFAULT_TEMPLATE;

    if (heroContentCardConfig?.showCallForAction) {
      heroContentCard.callForAction = {
        text: language.getMessage('open'),
        ariaLabel: language.getMessage('open'),
        isPrimary: false
      };
    }
    if (heroContentCardConfig?.showDelete) {
      heroContentCard.removeCourseDialogData = {
        title: language.getMessage('hero.removecourse.title'),
        description: language.getMessage('hero.removecourse.description'),
        primaryCtaText: language.getMessage('hero.removecourse.primaryCtaText'),
        secondaryCtaText: language.getMessage('hero.removecourse.secondaryCtaText')
      };
    }

    return heroContentCard;
  };

  getAiInputData = (config) => {
    const { language } = this.context.props;
    const { componentsToRender } = config || {};

    if (!componentsToRender?.showAiChatInput) return {};

    return {
      variant: AI_CHAT_VARIANT.B,
      title: language.getMessage('aichat.title1'),
      chatDescription: language.getMessage('aichat.chatDesc1')
    };
  };

  getRelatedChannelCardData = () => {
    const heroBook = this.getHeroBook() || {};
    const isRelatedMapping = CommonUtils.localStringCompare(
      heroBook.channel_mapping_type, CHANNEL_MAPPING_TYPES.RELATED
    );

    if (!isRelatedMapping || !heroBook.channel_id) return null;

    const { herobanner, language } = this.context.props || {};
    const {
      thumbnailUrl,
      title: channelTitle = '',
      isMostRelated
    } = herobanner.getChannelData(heroBook.channel_id) || {};

    if (!isMostRelated) return null;

    return {
      title: language.getMessage('relatedChannelCard.title'),
      thumbnailUrl,
      channelTitle,
      badgeText: language.getMessage('relatedChannelCard.badgeText')
    };
  };

  getMarketingCardData = () => {
    const {
      language
    } = this.context.props;
    const data = {
      title: language.getMessage('hero.marketing.title'),
      subTitle: this.context.getContainerData('heroView', 'marketing_subtitle') || language.getMessage('hero.marketing.subTitle'),
      primaryCTA: {
        text: language.getMessage('hero.emptystate.cta'),
        role: 'link'
      },
      coverImageUrl: `${env.PLUS_URL}/auth-home/media/images/eTextbook_marketing_image.png`
    };

    return data;
  };

  getPromoCardData = (isVariant1 = false) => {
    const { language } = this.context.props;
    const isHEIUser = CommonUtils.isHEIUser();
    let promoCardContent = {};

    if (isHEIUser) {
      promoCardContent = {
        title: language.getMessage('hero.mondly.learnLang.title'),
        subTitle: language.getMessage('hero.mondly.subTitle'),
        // eslint-disable-next-line max-len
        description: language.getMessage('hero.mondly.description'),
        btnText: language.getMessage('hero.mondly.startLearningCTA'),
        imgSrc: 'https://static.studychannel.pearsonprd.tech/components/channelswidget/mondlyByPearson.png',
        startLearningCTAUrl: 'https://www.mondly.com/offer/highered2',
        mondlyMarketingList: isVariant1 ? [
          {
            name: language.getMessage('hero.mondly.marketing.bestApp'),
            place: language.getMessage('hero.mondly.marketing.appStore'),
            imgSrc: `${env.PLUS_URL}/auth-home/media/images/mondlyBestApp.png`
          },
          {
            name: language.getMessage('hero.mondly.marketing.appYear'),
            place: language.getMessage('hero.mondly.marketing.fbStart'),
            imgSrc: `${env.PLUS_URL}/auth-home/media/images/mondlyEditorChoice.png`
          },
          {
            name: language.getMessage('hero.mondly.marketing.editorChoice'),
            place: language.getMessage('hero.mondly.marketing.googlePlay'),
            imgSrc: `${env.PLUS_URL}/auth-home/media/images/mondlyAppYear.png`
          }
        ] : null
      };
    } else if (this.context.commonPlugin.isNestTestAccess()) {
      promoCardContent.title = language.getMessage('hero.nestest.title');
      promoCardContent.subTitle = language.getMessage('hero.nestest.subTitle');
      promoCardContent.description = '';
      promoCardContent.btnText = language.getMessage('hero.learnmore');
      promoCardContent.startLearningCTAUrl = env.NEST_TEST.START_LEARNING_CTA_URL;
      promoCardContent.content = {
        headerText: null,
        list: [],
        footerText: null,
        isMondlyIconDisable: true
      };
    }

    return promoCardContent;
  };

  getHighlightPromoCard = () => {
    const promoCardContent = {};
    const { language, herobanner } = this.context.props;
    const { flags } = this.context.state;
    if (herobanner.channelId) {
      const {
        aiTutorUrl = CommonUtils.getChannelLink(herobanner.channelId, CHANNEL_HYPER_LINKS.AI_TUTOR)
      } = herobanner.getChannelData(herobanner.channelId) || {};

      if (aiTutorUrl) {
        promoCardContent.title = language.getMessage('hero.channelAI.title');
        promoCardContent.subTitle = language.getMessage('hero.channelAI.desc');
        promoCardContent.description = '';
        promoCardContent.btnText = language.getMessage('hero.channelAI.action');
        promoCardContent.startLearningCTAUrl = CommonUtils.getUrlWithUTMParams(aiTutorUrl, CHANNEL_GET_UNSTUCK);
        promoCardContent.content = {
          headerText: null,
          list: [],
          footerText: null,
          isMondlyIconDisable: true
        };
        promoCardContent.badgeText = null;
        promoCardContent.isPrimaryCTA = flags.channelAIPromoVariant === VARIANT_B;
        promoCardContent.variant = flags.channelAIPromoVariant;
        promoCardContent.iconName = 'AIChatBotIcon';
      }
    }

    return promoCardContent;
  };


  getChannelOnlyData = (config = {}) => {
    const { herobanner, language } = this.context.props;
    const {
      CHANNEL
    } = HERO_BANNER_TYPES;
    const {
      channelId,
      title
    } = HeroDashboardPlugin.activeCourseTab;
    const data = {};
    const channel = herobanner.getChannelData(channelId);
    const {
      componentsToRender
    } = config || {};
    const {
      showToc,
      showHeroContentCard
    } = componentsToRender || {};
    const heading = title;
    data.heroHeading = config.showHeroHeading ? heading : null;
    data.channelId = channelId || null;
    data.bannerType = CHANNEL;

    if (showHeroContentCard) {
      data.heroContentCard = this.constructChannelOnlyData({
        heroChannel: channel,
        config
      });
    }
    if (showToc) {
      data.tableOfContents = null;
      Framework.getEventManager().publish(CHANNEL_TOC_REQUESTED, { courseId: data.channelId });
    }

    return data;
  };

  getBookWithChannelData = (config = {}) => {
    const {
      BOOK_WITH_CHANNEL
    } = HERO_BANNER_TYPES;
    const {
      title
    } = HeroDashboardPlugin.activeCourseTab;
    const {
      language
    } = this.context.props || {};
    const { flags } = this.context.state;
    const data = {};
    const heroBook = this.getHeroBook();
    const {
      componentsToRender,
      heroContentCard: heroContentCardConfig
    } = config;
    const {
      showToc,
      showHeroContentCard
    } = componentsToRender || {};
    const {
      showGoToPage
    } = heroContentCardConfig || {};
    const heading = this.getHeroHeading(heroBook, title);
    const subHeading = this.getHeroSubHeading(heroBook);
    data.heroHeading = config.showHeroHeading ? heading : null;
    data.heroSubHeading = config.showHeroSubHeading ? subHeading : null;
    data.channelId = heroBook?.channel_id || null;
    data.bannerType = BOOK_WITH_CHANNEL;
    data.isAETEnabled = CommonUtils.isAETEnabled(heroBook, flags);
    const {
      tableOfContents,
      titleInfo,
      goToPage,
      ...heroBookData
    } = this.constructBookData({
      heroBook,
      config
    });

    data.titleInfo = titleInfo;
    if (showHeroContentCard) {
      data.heroContentCard = heroBookData;
    }
    data.tableOfContents = showToc ? tableOfContents : null;
    data.goToPage = showGoToPage ? goToPage : null;

    return data;
  };

  getEtextOnlyData = (config = {}) => {
    const {
      BOOK
    } = HERO_BANNER_TYPES;
    const {
      title
    } = HeroDashboardPlugin.activeCourseTab;
    const {
      language
    } = this.context.props || {};
    const { flags } = this.context.state;
    const data = {};
    const heroBook = this.getHeroBook();
    const {
      heroContentCard: heroContentCardConfig,
      componentsToRender
    } = config;
    const {
      showToc,
      showHeroContentCard
    } = componentsToRender || {};
    const {
      showGoToPage
    } = heroContentCardConfig || {};
    const heading = this.getHeroHeading(heroBook, title);
    const subHeading = this.getHeroSubHeading(heroBook);
    data.heroHeading = config.showHeroHeading ? heading : null;
    data.heroSubHeading = config.showHeroSubHeading ? subHeading : null;
    data.channelId = heroBook?.channel_id || null;
    data.bannerType = BOOK;
    data.isAETEnabled = CommonUtils.isAETEnabled(heroBook, flags);
    const {
      tableOfContents,
      titleInfo,
      goToPage,
      ...heroBookData
    } = this.constructBookData({
      heroBook,
      config,
      isEtextOnly: true
    });

    data.heroContentCard = showHeroContentCard ? heroBookData : null;
    data.titleInfo = titleInfo;
    data.tableOfContents = showToc ? tableOfContents : null;
    data.goToPage = showGoToPage ? goToPage : null;

    return data;
  };

  getHeroHeading = (heroBook, title) => {
    let heading = title;
    const { language } = this.context.props || {};
    const BMC = heroBook?.product_entitlements?.business_model_code;
    const courseId = heroBook?.course_id;
    const courseName = heroBook?.courseName;
    const isMyLabSubscription = CommonUtils.isMyLabSubscription(courseId, BMC);

    if (isMyLabSubscription && courseName) {
      heading = language.getMessage('hero.heroHeadingV1')?.replace('{replace_value}', courseName);
    }

    return heading;
  };

  // eslint-disable-next-line class-methods-use-this
  getHeroSubHeading = (heroBook) => {
    let heading = null;
    const courseName = heroBook?.courseName;

    if (courseName) {
      heading = courseName;
    }

    return heading;
  };

  getHeroHeaderCTA = () => {
    const { herobanner, language } = this.context.props;
    const { flags } = this.context.state;
    const { config } = getSnapshot(herobanner);
    const { heroHeader } = config || {};
    let CTAObject = null;
    if (heroHeader?.showCTA) {
      CTAObject = {
        text: flags.studyPrepCopyABTest
          ? language.getMessage('hero.header.ctav2')
          : language.getMessage('hero.header.cta')
      };
    }

    return CTAObject;
  };

  getHeroFooter = () => {
    let footerObject = null;
    const { language, herobanner } = this.context.props;
    const { flags } = this.context?.state;
    const practiceAssetVaraint = flags?.practiceAssetVaraint;
    const praticeCount = herobanner.getPracticeAssetsCount();
    if (CommonUtils.localStringCompare(practiceAssetVaraint, PRACTICE_ASSET_VARIANT.ON_TOC_FOOTER) && praticeCount) {
      footerObject = {
        text: language.getMessage('hero.practice.numOfUsers').replace(
          '{practice_num_of_users}', praticeCount
        ),
        cta: {
          text: language.getMessage('hero.practice.cta')
        }
      };
    }

    return footerObject;
  };

  getHeroDescription = () => {
    const { herobanner, language } = this.context.props;
    const { config } = getSnapshot(herobanner);
    const { heroHeader } = config || {};
    const { flags } = this.context.state;
    let heroDescription = null;

    if (heroHeader?.showDescription) {
      const heroBook = this.getHeroBook();
      const isExactLiteMapping = heroBook
      && this.context.coursePlugin.isExactLiteMapping(heroBook.channel_mapping_type);

      if (isExactLiteMapping) {
        heroDescription = language.getMessage('hero.heroBook.sectionSubHeadingV2');
      } else if (flags.studyPrepCopyABTest) {
        const {
          title
        } = herobanner.getChannelData(heroBook.channel_id) || {};
        heroDescription = CommonUtils.capitalize(title);
      } else {
        heroDescription = language.getMessage('hero.heroBook.sectionSubHeadingV1');
      }
    }

    return heroDescription;
  };

  getHeroTopicContainerHeading = () => {
    const { language } = this.context.props;
    const { flags } = this.context.state;

    const heroBook = this.getHeroBook();
    const isExactLiteMapping = heroBook
    && this.context.coursePlugin.isExactLiteMapping(heroBook.channel_mapping_type);
    let topicHeader = language.getMessage('hero.studyandexamprep');

    if (flags.studyPrepCopyABTest) {
      topicHeader = isExactLiteMapping
        ? language.getMessage('topicConatiner.exactLiteHeader')
        : language.getMessage('topicConatiner.otherHeader');
    }

    return topicHeader;
  };

  findBook({
    bookId,
    productId,
    courseId
  }) {
    let bookData = null;
    const { bookshelf } = this.context.props;
    const { books } = getSnapshot(bookshelf);
    bookData = books.find(book => (
      book.book_id === bookId
        && book.product_id === productId
        && book.course_id === courseId
    ));

    return bookData;
  }

  getConfig(template, props = {}) {
    const {
      BOOK,
      BOOK_WITH_CHANNEL,
      CHANNEL,
      MARKETING_TEMPLATE
    } = HERO_BANNER_TYPES;
    const { featureFlagsClient, herobanner } = this.context.props;
    const isInstructorCourseDashboardEnabled = this.context.instructorCourseDashboardPlugin?.isInstructorCourseDashboardEnabled();
    const heroBook = this.getHeroBook();
    const {
      channelId,
      showPopularChannels: overRideShowPopularChannels = true,
      isScreenResBelow1024
    } = props || {};
    const {
      aiTutorUrl,
      showExams = false,
      isMostRelated
    } = herobanner.getChannelData(channelId) || {};
    const isHEIUser = CommonUtils.isHEIUser();
    const { flags, hasExams } = this.context?.state;
    const {
      showAITutor,
      isAIReviewUser,
      enableAITutorOnAuthHome,
      channelAIPromoVariant,
      practiceAssetVaraint,
      studyPrepCopyABTest,
      enableRunDiagnostics,
      qrCodeVariant
    } = flags || {};

    const showHeroBatchPosition = studyPrepCopyABTest
      ? HERO_CONTENT_CARD_POSITIONS.BOTTOM : HERO_CONTENT_CARD_POSITIONS.TOP;
    const isHEIUserContent = isHEIUser && flags.showHEIUserContent;
    const isNestTestAccess = this.context.commonPlugin.isNestTestAccess();
    const hasChannelOrBundleSub = CommonUtils.isChannelsSubscriber();
    const { isAIStudyEnabled = true } = herobanner.getUserBookPreference() || {};
    const isAITutorEnabled = CommonUtils.isAHAITutorEnabled(
      enableAITutorOnAuthHome,
      showAITutor,
      isAIReviewUser,
      heroBook?.isAITutorSupported,
      isAIStudyEnabled,
      {
        bookId: heroBook?.book_id,
        productId: heroBook?.product_id,
        AIEnableDynamicSummaryBookList: flags?.AIEnableDynamicSummaryBookList,
        AIEnableDynamicSummary: flags?.AIEnableDynamicSummary
      }
    );
    const isNotPdf = !(heroBook && heroBook.product_model === ETEXT_PDF);
    const praticeCount = herobanner.getPracticeAssetsCount();
    const showPracticeSection = CommonUtils.localStringCompare(
      practiceAssetVaraint,
      PRACTICE_ASSET_VARIANT.ON_TOC_FLOW_TAB
    );
    const showFooterSection = CommonUtils.localStringCompare(
      practiceAssetVaraint,
      PRACTICE_ASSET_VARIANT.ON_TOC_FOOTER
    ) && praticeCount;

    const showHighlightPromoCard = !!channelAIPromoVariant
    && !(isAITutorEnabled && this.location === MLM_AUTH_HOME)
    && !!aiTutorUrl;

    const highlightPromoCard = channelAIPromoVariant === VARIANT_B ? { position: HERO_CONTENT_CARD_POSITIONS.TOP } : { position: HERO_CONTENT_CARD_POSITIONS.COL2 };
    const isPrimaryCTA = (showHighlightPromoCard ? channelAIPromoVariant !== VARIANT_B : true);
    const showRunDiagnostics = enableRunDiagnostics
      && CommonUtils.isLMSCourse()
      && this.context.launchContextId
      && isInstructorCourseDashboardEnabled ? heroBook?.controls?.diagnostics : heroBook?.showRunDiagnostics;
    const isQrCodeV4 = CommonUtils.localStringCompare(qrCodeVariant, QR_CODE_VARIANT.V4);
    const qrCodeBannerPosition = HERO_CONTENT_CARD_POSITIONS[isQrCodeV4 ? 'COL2' : 'COL1'];

    let config = {
      variant: DEFAULT_TEMPLATE,
      showHeroHeading: false,
      showHeroSubHeading: false,
      componentsToRender: {
        showHeroHeader: false,
        showHeroContentCard: false,
        showPromotionalCTA: false,
        showSubscriptionStatusCard: false,
        showPromotionalLinks: false,
        showAssets: false,
        showToc: false,
        showPromotionalCard: false,
        showPopularChannels: false,
        showMarketingCard: false,
        showExamPrepCard: false,
        showPromoCard: false,
        showPopularChannelSlider: false,
        showAiChatInput: false,
        showRelatedChannelCard: false,
        showHighlightPromoCard: false,
        showPracticeAssets: false,
        showFooter: false,
        showQrCode: false,
        showSchoolInfoCard: false
      },
      heroContentCard: {
        showInfo: false,
        showLinks: false,
        showGoToPage: false,
        showDelete: false,
        showCallForAction: false,
        position: '',
        showMyEtextbookLink: false
      },
      heroHeader: {
        showTitle: false,
        showCTA: false,
        showDescription: false,
        subscriptionStatusPosition: showHeroBatchPosition
      },
      promoCard: { position: MONDLY_CARD_POSITIONS.COL2 },
      highlightPromoCard: { position: HERO_CONTENT_CARD_POSITIONS.COL2 },
      qrCodeBanner: { position: qrCodeBannerPosition },
      examPrepCard: { hasExams },
      isNonMlmAeTHeroContext: CommonUtils.isNonMlmAeTHeroContext()
    };

    switch (template) {
      case BOOK:
        config = {
          ...config,
          showHeroHeading: true,
          showHeroSubHeading: false,
          componentsToRender: {
            showHeroContentCard: true,
            showSubscriptionStatusCard: true,
            showPromoCard: isHEIUserContent || isNestTestAccess,
            showPopularChannelSlider: !isScreenResBelow1024,
            showPromotionalCard: !hasChannelOrBundleSub,
            showAiChatInput: isNotPdf && isAITutorEnabled && this.location === MLM_AUTH_HOME,
            showRelatedChannelCard: !!isMostRelated && this.location !== MLM_AUTH_HOME,
            showQrCode: this.showQrCodeTestVariants(),
            showSchoolInfoCard: this.context.schoolInfoPlugin.showSchoolInfoCard()
          },
          heroContentCard: {
            showInfo: true,
            showLinks: CommonUtils.isActiveBook(heroBook),
            showGoToPage: this.showGoToPage({ heroBook }),
            showCallForAction: CommonUtils.isActiveBook(heroBook),
            position: HERO_CONTENT_CARD_POSITIONS.COL1,
            showMyEtextbookLink: false,
            variant: DEFAULT_TEMPLATE,
            showRunDiagnostics
          },
          heroAIchatCard: { position: MONDLY_CARD_POSITIONS.COL1 }
        };
        break;
      case BOOK_WITH_CHANNEL:
        config = {
          ...config,
          showHeroHeading: true,
          showHeroSubHeading: false,
          componentsToRender: {
            showHeroContentCard: true,
            showSubscriptionStatusCard: true,
            showPromotionalLinks: !isNestTestAccess && hasChannelOrBundleSub,
            showAssets: true,
            showToc: featureFlagsClient.variation('enableTocInHeroView', true),
            showExamPrepCard: showExams,
            showPromoCard: !showHighlightPromoCard && ((isHEIUserContent || isNestTestAccess)),
            showPopularChannelSlider: isHEIUserContent,
            showPromotionalCard: !hasChannelOrBundleSub,
            showAiChatInput: isNotPdf && isAITutorEnabled && this.location === MLM_AUTH_HOME,
            showHighlightPromoCard,
            showPracticeAssets: showPracticeSection,
            showFooter: showFooterSection,
            showQrCode: this.showQrCodeTestVariants(),
            showSchoolInfoCard: this.context.schoolInfoPlugin.showSchoolInfoCard()
          },
          heroContentCard: {
            showInfo: true,
            showGoToPage: this.showGoToPage({ heroBook }),
            showCallForAction: CommonUtils.isActiveBook(heroBook),
            position: isScreenResBelow1024 ? HERO_CONTENT_CARD_POSITIONS.TOP : HERO_CONTENT_CARD_POSITIONS.COL2,
            showMyEtextbookLink: false,
            variant: DEFAULT_TEMPLATE,
            showRunDiagnostics
          },
          heroHeader: {
            showTitle: true,
            showCTA: true,
            showDescription: !HeroDashboardPlugin.isBundleUser,
            showSubscriptionStatus: hasChannelOrBundleSub,
            isPrimaryCTA,
            subscriptionStatusPosition: showHeroBatchPosition
          },
          heroAIchatCard: { position: MONDLY_CARD_POSITIONS.COL2 },
          promoCard: { position: MONDLY_CARD_POSITIONS.COL2 },
          highlightPromoCard,
          examPrepCard: { hasExams }
        };
        break;
      case CHANNEL:
        config = {
          ...config,
          showHeroHeading: true,
          componentsToRender: {
            showHeroContentCard: true,
            showSubscriptionStatusCard: true,
            showPromotionalLinks: hasChannelOrBundleSub,
            showAssets: true,
            showToc: true,
            showExamPrepCard: showExams,
            showPromoCard: isHEIUserContent,
            showPopularChannelSlider: isHEIUserContent,
            showPromotionalCard: !hasChannelOrBundleSub,
            showHighlightPromoCard,
            showPracticeAssets: showPracticeSection,
            showFooter: showFooterSection,
            showQrCode: this.showQrCodeTestVariants()
          },
          heroContentCard: {
            showDelete: true,
            showCallForAction: true,
            position: HERO_CONTENT_CARD_POSITIONS.COL2,
            variant: DEFAULT_TEMPLATE
          },
          heroHeader: {
            showTitle: true,
            showCTA: true,
            showSubscriptionStatus: hasChannelOrBundleSub,
            isPrimaryCTA,
            subscriptionStatusPosition: showHeroBatchPosition
          },
          promoCard: { position: MONDLY_CARD_POSITIONS.COL2 },
          highlightPromoCard,
          examPrepCard: { hasExams }
        };
        break;
      case MARKETING_TEMPLATE:
        config = {
          ...config,
          componentsToRender: {
            showPopularChannels: overRideShowPopularChannels,
            showMarketingCard: !isHEIUser,
            showPromoCard: isHEIUser,
            showPopularChannelSlider: isHEIUserContent && !flags.showHEIUserContent
          },
          promoCard: { position: MONDLY_CARD_POSITIONS.COL1 }
        };
        break;
      default:
        break;
    }

    return config;
  }

  getHeroBook = () => {
    const {
      bookId,
      productId,
      courseId
    } = HeroDashboardPlugin.activeCourseTab || {};
    const heroBook = this.findBook({ bookId, productId, courseId });

    return heroBook;
  };

  getStudyPrepOrPackText = () => {
    const { language } = this.context.props;

    return language.getMessage('subscription.studyTitle');
  };

  getSubscriptionText = () => {
    const { language } = this.context.props;
    const text = CommonUtils.isChannelsSubscriber()
      ? 'subscriptiontext.isSubscribed'
      : 'subscriptiontext.nonSubscriber';

    return language.getMessage(text);
  };

  showGoToPage = ({
    heroBook
  }) => {
    const isActiveHeroBook = CommonUtils.isActiveBook(heroBook);

    if (!isActiveHeroBook) {
      return false;
    }

    // eslint-disable-next-line camelcase
    const { printPageAvailable, book_type } = heroBook;
    const { flags } = this.context?.state;
    const { featureFlagsClient } = this.context?.props;
    // eslint-disable-next-line camelcase
    const isBronte = book_type === CONTENT_TYPE_EPUB;
    // eslint-disable-next-line camelcase
    const isCITE = book_type === CONTENT_TYPE_CITE;
    const isBVBronte = ProductUtils.isBVBronteBook(book_type);

    let showGoToPage = false;

    if (flags?.enableGoToPage && printPageAvailable) {
      if (isBronte) {
        showGoToPage = featureFlagsClient.variation('enablePrintPageNavigationBronte', false);
      } else if (featureFlagsClient.variation('enablePrintPageNavigation', false)
        && !isCITE
        && !isBVBronte
      ) {
        showGoToPage = true;
      }
    }

    return showGoToPage;
  };

  /**
  * Function to set falgs
  * @param {array} courses
  */
  getCourse = (courses) => {
    let courseDetails = {
      course_id: null,
      course_name: null,
      course_start_date: null,
      course_end_date: null,
      user_role: null
    };
    if (courses && Array.isArray(courses) && courses.length > 0) {
      const course = courses[0];
      courseDetails = { ...courseDetails, ...course };
    }

    return courseDetails;
  };

  setNotificationData = (titleInfo, overRideNotification = false) => {
    const {
      herobanner
    } = this.context.props || {};
    const isNotificationSet = titleInfo && titleInfo.notificationsData && !overRideNotification;
    const heroBook = this.getHeroBook();
    const heroNotification = heroBook && heroBook.bannerData;

    if (!isNotificationSet && heroNotification) {
      herobanner.setNotification(heroNotification);
    }
  };

  getProductModelName = (titleInfo) => {
    const productModelName = titleInfo && titleInfo.productModelName;

    if (productModelName) {
      return productModelName;
    }

    const heroBook = this.getHeroBook();

    return (heroBook && heroBook.product_model_name) || null;
  };

  setSubscriptionStatusData = (props = {}) => {
    const { user, herobanner } = this.context?.props || {};
    const {
      subscriptionStatus,
      config
    } = props || {};
    const {
      componentsToRender
    } = config || {};
    const { dataFetched } = this.context?.state || {};
    const isUserFetched = user && user.gpsSubscriptions;
    const heroBook = this.getHeroBook() || {};
    const data = this.getSubscriptionStatusData({ heroBook });
    const isSubscriptionStatusSet = subscriptionStatus?.listContent?.length > 0;

    if (isUserFetched && dataFetched && !isSubscriptionStatusSet && componentsToRender?.showSubscriptionStatusCard) {
      herobanner.setSubscriptionStatus(data);
    }
  };

  /**
   * Constructs and returns subscription status data
   *
   * @param {Object} param0
   * @returns
   */
  getSubscriptionStatusData = (props = {}) => {
    const { heroBook } = props || {};
    const { language } = this.context.props;
    const isBundleUser = HeroDashboardPlugin.isBundleUser;
    const hasChannelSubscription = HeroDashboardPlugin.hasChannelSubscription;
    const subscriptions = [];
    const title = language.getMessage('hero.subscriptionStatusTitle2');

    let hasActiveBooks = false;

    if (!heroBook || CommonUtils.isEmpty(heroBook)) {
      const { bookshelf } = this.context.props || {};
      const { books } = getSnapshot(bookshelf) || {};
      hasActiveBooks = CommonUtils.hasActiveBooks(books);
    }

    const isEtextbookChecked = !CommonUtils.isEmpty(heroBook) ? true : hasActiveBooks;

    /**
     * For all users eTextbook
     *
     */
    subscriptions.push({
      id: PPLUS_SUBSCRIPTIONS.ETEXTBOOK,
      text: language.getMessage('hero.eTextbook'),
      checked: isEtextbookChecked,
      isClickable: !isEtextbookChecked
    });

    /**
      * For all other users - Study & Exam Prep
      *
    */
    if (!isBundleUser) {
      subscriptions.push({
        id: PPLUS_SUBSCRIPTIONS.STUDY_EXAM_PREP,
        text: language.getMessage('hero.studyandexamprep'),
        checked: hasChannelSubscription,
        isClickable: true
      });
    }

    /**
     * If bundle user copy - Study & Exam Prep Pack
     *
    */
    if (isBundleUser) {
      subscriptions.push({
        id: PPLUS_SUBSCRIPTIONS.STUDY_EXAM_PREP_PACK,
        text: language.getMessage('hero.heroHeadingForBundleUser'),
        checked: isBundleUser,
        isClickable: true
      });
    }

    return {
      title,
      listContent: subscriptions
    };
  };

  getSubscriptionStatusCardCta = (bannerType) => {
    const { language } = this.context.props;
    let cta = null;

    if (bannerType === HERO_BANNER_TYPES.BOOK) {
      cta = {
        text: HeroDashboardPlugin.isBundleUser ? language.getMessage('subscriptionstatus.bundlecta')
          : language.getMessage('subscriptionstatus.nonbundlecta'),
        isPrimary: true
      };
    }

    return cta;
  };

  /**
   * Sets table of content data for hero book
   *
   */
  setTableOfContentData() {
    let data = {};
    let book = null;
    const { asset, herobanner } = this.context.props;
    const { tableOfContents } = getSnapshot(herobanner);
    let chapterId = (tableOfContents && tableOfContents.chapterId) || '';
    book = this.findHeroBook();
    if (book) {
      const isPdf = book.product_model === ETEXT_PDF;
      const role = ((
        book.courses
        && book.courses.length > 0
        && book.courses[0].user_role)
        || (book.product_entitlements?.user_role
        ));
      const isAudioBook = book.showAudio;
      if (isPdf) {
        data = asset.getAssetTocPdf(null, role, false, isAudioBook);
      } else {
        data = getSnapshot(asset);
      }
      data = this.tocTraverseToMapUniqueId(data);
      chapterId = this.userLastVisitedId || chapterId;
      herobanner.setTableOfContents({ data, chapterId });
    }
  }

  constructChannelTOC = (tocData, userRecentTopicId) => {
    const tocNode = {};
    tocNode.id = tocData.id;
    tocNode.title = tocData.title;

    if (userRecentTopicId === tocData.id) {
      this.userChannelRecentTopic.id = tocData.id;
      this.userChannelRecentTopic.title = tocData.title;
    }

    if (tocData.topics?.length > 0) {
      tocNode.children = [];
      tocData.topics.forEach((topicData) => {
        tocNode.children.push(this.constructChannelTOC(topicData, userRecentTopicId));
      });
    }

    return tocNode;
  };

  setChannelTOCData(response = {}) {
    const {
      userRecentTopicId,
      chapters
    } = response || {};
    const { herobanner } = this.context.props || {};
    let chapterId = null;
    let chapterTitle = null;
    const data = {};
    data.children = [];

    if (chapters && chapters.length > 0) {
      let firstTopic = null;
      chapters.forEach((tocData) => {
        if (!firstTopic && tocData.topics?.length) {
          firstTopic = tocData.topics[0];
        }

        if (tocData.topics?.length > 0) {
          data.children.push(this.constructChannelTOC(tocData, userRecentTopicId));
        }
      });

      const isNewUser = !userRecentTopicId || CommonUtils.isEmpty(this.userChannelRecentTopic || {});

      const chapterData = !isNewUser ? this.userChannelRecentTopic : firstTopic;
      const {
        id,
        title
      } = chapterData;

      chapterId = id;
      chapterTitle = title;
    }

    herobanner.setTableOfContents({
      data,
      chapterId,
      chapterTitle,
      isVariant2: true
    });
  }

  /**
   * Sets isTOCFetchFailed as true when asset call fails
   *
   */
  setTableOfContentError() {
    const { herobanner } = this.context.props;
    herobanner.setTableOfContents({ isTOCFetchFailed: true });
  }

  /**
   * Find the Hero book in library
   *
   * @returns
   */
  findHeroBook() {
    let bookData;
    const { BOOK, BOOK_WITH_CHANNEL } = HERO_BANNER_TYPES;
    const { bookshelf, herobanner } = this.context.props;
    const { books } = getSnapshot(bookshelf);
    const { bannerType, titleInfo } = herobanner;
    if (bannerType === BOOK || bannerType === BOOK_WITH_CHANNEL) {
      bookData = books.find(book => (
        book.book_id === titleInfo.book_id
          && book.product_id === titleInfo.product_id
          && book.course_id === titleInfo.course_id
      ));
    }

    return bookData;
  }

  /**
   * TO Traverse tree and update unque id with separator
   *
   * @param {*} tocData
   */
  tocTraverseToMapUniqueId(tocChildren) {
    const { herobanner } = this.context.props;
    const { tableOfContents } = getSnapshot(herobanner);
    const chapterId = (tableOfContents && tableOfContents.chapterId) || '';
    const tocLevelChildList = {};
    tocLevelChildList.id = tocChildren.id;
    tocLevelChildList.title = tocChildren.title;
    tocLevelChildList.children = [];
    (tocChildren.children).forEach((tocData, parentIndex) => {
      if (typeof tocData.display === 'undefined' || tocData.display) {
        const childList = this.constructTree(tocData, (parentIndex + 1), chapterId);
        tocLevelChildList.children.push(childList);
      }
    });

    return tocLevelChildList;
  }

  /**
   * Method for constructing tree structured TOC
   *
   * @param {*} tocData
   * @returns
   */
  constructTree(tocData, parentIndex, chapterId) {
    const tocNode = {};
    if (tocData.id === chapterId) {
      this.userLastVisitedId = `${tocData.id}${TOC_SEPERATOR_CHAPTER}${parentIndex}`;
    }
    tocNode.id = `${tocData.id}${TOC_SEPERATOR_CHAPTER}${parentIndex}`;
    tocNode.title = tocData.title;
    tocNode.children = [];
    if (tocData.children) {
      tocData.children.forEach((tocDataChildren, tocDataChildrenIndex) => {
        if (typeof tocDataChildren.display === 'undefined' || tocDataChildren.display) {
          const childNode = this.constructTree(tocDataChildren, `${parentIndex}.${(tocDataChildrenIndex + 1)}`, chapterId);
          tocNode.children.push(childNode);
        }
      });
    }

    return tocNode;
  }

  static isTocFetched = () => {
    const heroBannerStore = Framework.getStoreRegistry().getStore('herobanner');
    const heroBanner = getSnapshot(heroBannerStore);

    return !!heroBanner?.TableOfContents?.data;
  };

  // eslint-disable-next-line class-methods-use-this
  getHeroStyles = (
    template = HERO_BANNER_TYPES.MARKETING_TEMPLATE
  ) => HERO_STYLES[template];

  /**
   * Makes asset call for the hero book required for TOC
   *
   * @param {Object} book
   */
  fetchAsset(book) {
    const { asset } = this.context.props;
    const id = CommonUtils.isIntegratedBMC(book.product_entitlements.business_model_code)
      ? book.book_id : book.product_id;


    // reset loading state
    asset.setLoading(false);

    if (book) {
      asset.fetch(id, book.course_id, null, book.book_type, false);
    }
  }

  /**
   * To get clean chapterId in case of duplicate case
   *
   */
  getCleanChapterId = () => {
    const { herobanner } = this.context.props;
    const { tableOfContents } = getSnapshot(herobanner);
    let renderChapterId = (tableOfContents && tableOfContents.chapterId) || '';
    if (renderChapterId && renderChapterId.includes(TOC_SEPERATOR_CHAPTER)) {
      renderChapterId = renderChapterId.split(TOC_SEPERATOR_CHAPTER)[0];
    }

    if (tableOfContents && (renderChapterId === tableOfContents.initialChapterId)) {
      renderChapterId = '';
    }

    return renderChapterId ? encodeURI(renderChapterId) : '';
  };

  /**
   * Returns the current go to page number of hero book
   *
   * @returns
   */
  getGoToPageNo = () => {
    const { herobanner } = this.context.props;
    const { goToPage } = getSnapshot(herobanner);
    const { value, initialValue } = goToPage || {};
    let renderValue = value;

    if (renderValue?.includes('-')) {
      renderValue = renderValue.replace(/^-+|-+$/g, '');
    }

    return encodeURI(initialValue === renderValue ? encodeURI('') : encodeURI(renderValue));
  };

  handleCourseTabEvents = (fromLocation = '') => {
    if (fromLocation === LOCATION_IN_APP.LIBRARY_L2) {
      return;
    }
    const {
      template = HERO_BANNER_TYPES.MARKETING_TEMPLATE,
      channelId,
      channelMappingType,
      productId = null
    } = HeroDashboardPlugin.activeCourseTab || {};
    const {
      flags
    } = this.context.state || {};
    const eventLabel = this.isInitialLoad
      ? COURSE_TAB_EVENT.LOADED_COURSE_EVENT_LABEL : COURSE_TAB_EVENT.OTHER_COURSE_EVENT_LABEL;
    const isRelatedMapping = CommonUtils.localStringCompare(
      channelMappingType || '', CHANNEL_MAPPING_TYPES.RELATED
    );
    const isExactLiteMapping = flags.enableExactLiteMapping && CommonUtils.localStringCompare(
      channelMappingType || '', CHANNEL_MAPPING_TYPES.EXACTLITE
    );
    let currentTemplate = template;

    if (isExactLiteMapping) currentTemplate = ETEXT_MAPPEDCHANNEL_LITE;
    else if (isRelatedMapping) currentTemplate = ETEXT_RELATED_CHANNEL;

    const customFields = {
      container_value: COURSE_TAB_EVENT[currentTemplate],
      channel_id: channelId || null
    };
    customFields.book_id = HeroDashboardPlugin?.activeCourseTab?.bookId || null;
    customFields.product_id = productId;
    CommonUtils.dispatchGaEvent(
      AUTHHOME_APPLICATION,
      EVENT_USER_INTERACTION,
      COURSE_TAB_EVENT.COURSE_TAB_CLICK_EVENT_ACTION,
      eventLabel,
      customFields
    );
  };

  /**
   * *********************************************
   * Callbacks
   * ********************************************
   */


  /**
   * Handle TOC click
   */
  handleTocClick = (fetchAsset = true) => {
    const { herobanner } = this.context.props || {};
    const {
      bannerType
    } = getSnapshot(herobanner);
    const {
      BOOK,
      BOOK_WITH_CHANNEL
    } = HERO_BANNER_TYPES;
    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      location_in_app: LOCATION_IN_APP.COURSE_TAB
    };
    const book = this.findHeroBook();
    if (fetchAsset && book && (bannerType === BOOK || bannerType === BOOK_WITH_CHANNEL)) {
      this.fetchAsset(book);
    }
    CommonUtils.dispatchGaEvent(
      LIBRARY_EVENT.EVENT_CATEGORY,
      EVENT_AUTHHOMEEVENT,
      LIBRARY_EVENT.ETEXT_FEATURE_ELEMENT_CLICK_EVENT_ACTION,
      LIBRARY_EVENT.TOC_EVENT_LABEL,
      customFields
    );
    this.handleCourseTabEvents();
  };

  handleChapterIdChange = (chapterId, chapterTitle, parentChapterTitle) => {
    const { herobanner } = this.context.props;

    herobanner.setTableOfContents({ chapterId, chapterTitle, parentChapterTitle });
    this.context.studyHelpPlugin.fetchHeroMappedChannel();
  };

  /**
   * Handles CTA click in Hero
   *
   * @param {*} isFromInfoModal
   */
  handleCtaClick = (isFromInfoModal = false, assignmentId = null) => {
    const {
      BOOK_WITH_CHANNEL,
      BOOK,
      CHANNEL
    } = HERO_BANNER_TYPES;
    const { herobanner } = this.context.props;
    const { bannerType, channelId } = getSnapshot(herobanner);

    switch (bannerType) {
      case BOOK_WITH_CHANNEL:
      case BOOK: {
        const book = this.findHeroBook();
        if (book) {
          const goToPageNo = this.getGoToPageNo();
          this.context.handleBookClick(
            book,
            READ,
            book.course_id,
            null,
            null,
            LOCATION_IN_APP.COURSE_TAB,
            { goToPageNo, assignmentId }
          );
          CommonUtils.handleInfoBookCTAEvents(book, true, LOCATION_IN_APP.COURSE_TAB, isFromInfoModal);
        }
      }
        break;
      case CHANNEL: {
        const {
          url,
          learnUrl,
          exploreUrl,
          showExams
        } = herobanner.getChannelData(channelId);
        this.tiggerChannelCardElement(CHANNEL_CARD_EVENT.CHANNEL_CONTAINER_FIELD);
        this.handleCourseTabEvents();
        if (url || learnUrl || (url && showExams) || exploreUrl) {
          const renderURL = url || learnUrl || CommonUtils.getExamPrepUrl(url) || exploreUrl;
          const studyPrepUrl = `${renderURL}?${CHANNEL_CHANNEL_QUERY_PARAM}`;
          if (this.context.commonPlugin.isIntegratedUI()) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
          } else {
            window.open(studyPrepUrl);
          }
        }
        this.context.commonPlugin.handleChannelCardClick();
      }
        break;
      default:
        break;
    }
  };

  /**
   * Handles thumbnail click in Hero
   */
  handleHeroThumbnailClick = () => {
    const heroBook = this.getHeroBook();

    if (!CommonUtils.isActiveBook(heroBook)) {
      return;
    }

    this.handleCtaClick();
  };

  /**
   * Handles title info modal CTA click
   */
  onTitleInfoBookCTAClick = () => {
    this.handleCtaClick(true);
  };

  /**
   * Handles handle Icons Click on Hero
   */
  handleIconsClick = (type) => {
    const book = this.findHeroBook();
    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      location_in_app: LOCATION_IN_APP.COURSE_TAB
    };

    if (type === ACTIVITY_TYPE.REMOVE_FROM_COURSE) {
      // Instrumentation
      CommonUtils.dispatchGaEvent(
        LIBRARY_EVENT.EVENT_CATEGORY,
        EVENT_AUTHHOMEEVENT,
        LIBRARY_EVENT.ETEXT_FEATURE_ELEMENT_CLICK_EVENT_ACTION,
        LIBRARY_EVENT.REMOVE_ICON_EVENT_LABEL, customFields
      );
    } else if (type === ACTIVITY_TYPE.INFO) {
      CommonUtils.dispatchGaEvent(
        LIBRARY_EVENT.EVENT_CATEGORY,
        EVENT_AUTHHOMEEVENT,
        LIBRARY_EVENT.ETEXT_FEATURE_ELEMENT_CLICK_EVENT_ACTION,
        LIBRARY_EVENT.INFO_EVENT_LABEL, customFields
      );
    } else if (book && type !== ACTIVITY_TYPE.INFO) {
      this.context.handleBookClick(book, type, book.course_id, null, null, LOCATION_IN_APP.COURSE_TAB);
    }
  };

  /**
   * Handles title info Notification primary or secondary CTA click
   *
   * @param {*} event
   * @param {*} isDismissed
   * @param {*} launchURL
   * @param {*} target
   * @param {*} bannerId
   */
  onTitleInfoNotificationCTAClick = (event, isDismissed, launchURL, target, bannerId) => {
    const book = this.findHeroBook();
    this.context.handleSubscriptionBannerClick(
      event,
      book,
      isDismissed,
      launchURL,
      target,
      bannerId,
      LOCATION_IN_APP.HERO
    );
    this.setNotificationData(null, true);
  };

  /**
   * Handles title info modal Remove from library CTA click
   */
  onTitleInfoRemoveClick = () => {
    const book = this.findHeroBook();

    this.context.handleRemoveTitlePopup(book, null, true, LOCATION_IN_APP.HERO);
  };

  /**
   * Handles Go to page number text field change
   *
   * @param {String} updatedGoToPageNo
   */
  onGoToPageNoChange = (updatedGoToPageNo) => {
    const { herobanner } = this.context.props;
    let sanitizedGoToPageNo = xss(updatedGoToPageNo);
    if (sanitizedGoToPageNo?.includes('-')) sanitizedGoToPageNo = sanitizedGoToPageNo?.replace(/^-+/g, '');

    herobanner.setGoToPage(sanitizedGoToPageNo);
  };

  /**
   * Handles go to page input box blur
   *
   */
  onGoToPageInputBlur = () => {
    const { herobanner } = this.context.props;
    const { goToPage } = getSnapshot(herobanner);
    const { value } = goToPage;
    let renderValue = value;

    if (!renderValue?.includes('-')) return;

    renderValue = renderValue.replace(/^-+|-+$/g, '');

    if (renderValue?.length > 0) {
      herobanner.setGoToPage(renderValue);
    }
  };

  onAssetCardClick = (cardIndex, cardProps = {}) => {
    const {
      url
    } = cardProps;
    const { activeTocAssetFlow } = this.context.state;
    let locationInApp = CHANNEL_CARD_EVENT.RECOMMENDED_CONTAINER_FIELD;
    const isPracticeFlow = activeTocAssetFlow === ASSET_TABS_VALUE.PRACTICE;
    const channelQueryParam = isPracticeFlow ? CHANNEL_ASSET_PRATICE_QUERY_PARAM : CHANNEL_ASSET_QUERY_PARAM;
    if (isPracticeFlow) {
      locationInApp = LOCATION_IN_APP.PRACTICE;
    }

    this.tiggerChannelCardElement(locationInApp);
    if (url && CommonUtils.isAbsoluteURL(url)) {
      const assetUrl = CommonUtils.getUrlWithUTMParams(url, channelQueryParam);
      if (this.context.commonPlugin.isIntegratedUI()) {
        this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, assetUrl);
      } else {
        window.open(assetUrl);
      }
    } else {
      const assetUrl = CommonUtils.getUrlWithUTMParams(`${env.STUDY_CHANNEL_URL}${url}`, channelQueryParam);
      if (this.context.commonPlugin.isIntegratedUI()) {
        this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, assetUrl);
      } else {
        window.open(assetUrl);
      }
    }
    this.handleCourseTabEvents();
    this.context.commonPlugin.handleChannelCardClick();
  };

  onHeroHeaderCTAClick = () => {
    const { herobanner } = this.context.props;
    const {
      MARKETING_TEMPLATE,
      CHANNEL,
      BOOK_WITH_CHANNEL
    } = HERO_BANNER_TYPES;
    const {
      bannerType,
      channelId
    } = getSnapshot(herobanner);
    const {
      url = CommonUtils.getChannelLink(channelId),
      learnUrl = CommonUtils.getChannelLink(channelId, CHANNEL_HYPER_LINKS.LEARN),
      exploreUrl,
      showExams
    } = herobanner.getChannelData(channelId) || {};
    const { EVENT_CATEGORY, ETEXT_CLICK_EVENT_ACTION, EXEXT_VIEW_ETEXTBOOK_EVENT_LABEL } = LIBRARY_EVENT;

    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      location_in_app: LOCATION_IN_APP.COURSE_TAB,
      container_value: CommonUtils.getCurrentScreenEventLabel(this.context.state.flags)
    };

    const locationInApp = CHANNEL_CARD_EVENT.PRIMARY_CTA_FIELD;

    this.tiggerChannelCardElement(locationInApp);
    const studyToolUrl = learnUrl || url || CommonUtils.getExamPrepUrl(url) || exploreUrl;
    switch (bannerType) {
      case MARKETING_TEMPLATE:
        CommonUtils.dispatchGaEvent(EVENT_CATEGORY, EVENT_AUTHHOMEEVENT, ETEXT_CLICK_EVENT_ACTION, EXEXT_VIEW_ETEXTBOOK_EVENT_LABEL, customFields);
        window.open(env.BOOKSHELF_CONFIG.purchase);
        break;
      case BOOK_WITH_CHANNEL:
      case CHANNEL:
        if (studyToolUrl) {
          const studyPrepUrl = `${studyToolUrl}?${CHANNEL_QUERY_PARAM}`;
          if (this.context.commonPlugin.isIntegratedUI()) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
          } else {
            window.open(studyPrepUrl);
          }
        }
        break;
      default:
        break;
    }
    if (bannerType !== MARKETING_TEMPLATE) this.handleCourseTabEvents();

    this.context.commonPlugin.handleChannelCardClick();
  };

  onPromotionalCardCTAClick = () => {
    const { herobanner } = this.context.props;
    const { BOOK } = HERO_BANNER_TYPES;
    const { bannerType } = getSnapshot(herobanner);

    const studyPrepUrl = `${env.STUDY_CHANNEL_URL}?CEP=plusAuthHP_Generic`;
    if (this.context.commonPlugin.isIntegratedUI() && bannerType !== BOOK) {
      this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
    } else {
      window.open(studyPrepUrl);
    }
    this.context.commonPlugin.handleChannelCardClick();
  };

  onPromotionalLinksClick = (id = '') => {
    const { herobanner } = this.context.props;
    const {
      channelId
    } = getSnapshot(herobanner);
    const {
      learnUrl = CommonUtils.getChannelLink(channelId, CHANNEL_HYPER_LINKS.LEARN),
      aiTutorUrl = CommonUtils.getChannelLink(channelId, CHANNEL_HYPER_LINKS.AI_TUTOR),
      showExams,
      url = CommonUtils.getChannelLink(channelId)
    } = herobanner.getChannelData(channelId) || {};
    this.tiggerChannelCardElement(CHANNEL_CARD_EVENT.STUDY_EXAMPREP_CARD_FIELD);

    switch (id) {
      case CHANNEL_LINKS.LEARN:
        // eslint-disable-next-line no-case-declarations
        const learnUrlWithParams = `${learnUrl}?${CHANNEL_SECTION_QUERY_PARAM}`;
        if (learnUrl) {
          if (this.context.commonPlugin.isIntegratedUI()) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, learnUrlWithParams);
          } else {
            window.open(learnUrlWithParams);
          }
        }
        break;
      case CHANNEL_LINKS.AI_TUTOR:
        // eslint-disable-next-line no-case-declarations
        const aiTutorUrlWithParams = `${aiTutorUrl}?${CHANNEL_SECTION_QUERY_PARAM}`;
        if (aiTutorUrl) {
          if (this.context.commonPlugin.isIntegratedUI()) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, aiTutorUrlWithParams);
          } else {
            window.open(aiTutorUrlWithParams);
          }
        }
        break;
      case CHANNEL_LINKS.EXAM_PREP:
        // eslint-disable-next-line no-case-declarations
        const examPrepUrl = `${CommonUtils.getExamPrepUrl(url)}?${CHANNEL_SECTION_QUERY_PARAM}`;
        if (showExams) {
          if (this.context.commonPlugin.isIntegratedUI()) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, examPrepUrl);
          } else {
            window.open(examPrepUrl);
          }
        }
        break;
      default:
        break;
    }
    this.handleCourseTabEvents();
    this.context.commonPlugin.handleChannelCardClick();
  };

  onSubStatusCardLinksClick = (id) => {
    const { herobanner } = this.context.props;
    const {
      CHANNEL,
      BOOK_WITH_CHANNEL,
      BOOK
    } = HERO_BANNER_TYPES;
    const {
      bannerType,
      channelId
    } = getSnapshot(herobanner);
    const {
      url
    } = herobanner.getChannelData(channelId) || {};

    switch (id) {
      case PPLUS_SUBSCRIPTIONS.ETEXTBOOK:
        window.open(env.BOOKSHELF_CONFIG.purchase);
        break;
      case PPLUS_SUBSCRIPTIONS.STUDY_EXAM_PREP:
      case PPLUS_SUBSCRIPTIONS.STUDY_EXAM_PREP_PACK:
        this.tiggerChannelCardElement(CHANNEL_CARD_EVENT.STATUS_CARD_FIELD);
        this.handleCourseTabEvents();
        if ((bannerType === BOOK_WITH_CHANNEL || bannerType === CHANNEL) && url) {
          const studyPrepUrl = CommonUtils.getUrlWithUTMParams(url, STUDY_EXAM_PREP_SUBSCRIPTION_STATUS_PARAM);
          if (this.context.commonPlugin.isIntegratedUI() && bannerType !== BOOK) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
          } else {
            window.open(studyPrepUrl);
          }
        } else {
          const studyPrepUrl = CommonUtils.getUrlWithUTMParams(`${env.STUDY_CHANNEL_URL}/subscription`, STUDY_EXAM_PREP_SUBSCRIPTION_STATUS_PARAM);
          if (this.context.commonPlugin.isIntegratedUI() && bannerType !== BOOK) {
            this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
          } else {
            window.open(studyPrepUrl);
          }
        }
        break;
      default:
        break;
    }
    this.context.commonPlugin.handleChannelCardClick();
  };

  onRemoveCourse = () => {
    const { channelId } = HeroDashboardPlugin.activeCourseTab || {};
    Framework.getEventManager().publish(COURSE_COLLECTION_SERVICE_REQUESTED,
      { method: HTTP_REQUEST_METHODS.DELETE, payload: { name: channelId } });
  };

  onHEIStartLearningClick = (url) => {
    let eventLabel = HEADER_EVENT.PROMO_LEARN_LANGUAGE_HEI_USER_LABEL;

    if (this.context.commonPlugin.isNestTestAccess()) {
      eventLabel = HEADER_EVENT.PROMO_LEARN_LANGUAGE_NESTEST_USER_LABEL;
    }

    CommonUtils.dispatchGaEvent(AUTHHOME_APPLICATION, EVENT_USER_INTERACTION, HEADER_EVENT.HEADER_CLICK_EVENT_ACTION, eventLabel, null);
    window.open(url, '_black');
  };

  onPopularChannelCardClick = (index) => {
    if (this.location === MLM_AUTH_HOME) {
      this.onMLMPopularChannelCardClick(index);
    } else {
      this.context.coursePlugin.handlePopularChannelCardClick(index);

      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  tiggerChannelCardElement = (locationInApp, isInitialLoad = false) => {
    const { flags } = this.context.state;
    CommonUtils.tiggerViewChannelsCardEvent(locationInApp, isInitialLoad, flags);
  };

  tiggerABTestingEvent = (bannerType, isInitialLoad) => {
    if (!isInitialLoad) {
      return;
    }
    const isHEIUser = CommonUtils.isHEIUser();
    const { flags } = this.context?.state;

    if (isHEIUser && bannerType !== HERO_BANNER_TYPES.MARKETING_TEMPLATE) {
      const eventLabel = flags.showHEIUserContent
        ? ONBOARDING_HEI_AB_TESTING_EVENT.AB_TESTING_GROUP_B_EVENT_LABEL
        : ONBOARDING_HEI_AB_TESTING_EVENT.AB_TESTING_GROUP_A_EVENT_LABEL;
      CommonUtils.logABTestingEvent(eventLabel, ONBOARDING_HEI_AB_TESTING_EVENT.HEI_AH_FIELD);
    }
  };

  onSubStatusCardCTAClick = () => {
    const { herobanner } = this.context.props;
    const { BOOK } = HERO_BANNER_TYPES;
    const { bannerType } = getSnapshot(herobanner);

    this.tiggerChannelCardElement(CHANNEL_CARD_EVENT.PRIMARY_CTA_FIELD);
    const studyPrepUrl = `${env.STUDY_CHANNEL_URL}?CEP=plusAuthHP_Generic`;
    if (this.context.commonPlugin.isIntegratedUI() && bannerType !== BOOK) {
      this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, studyPrepUrl);
    } else {
      window.open(studyPrepUrl);
    }
    this.context.commonPlugin.handleChannelCardClick();
  };

  onAIChatbotSend = (chatText) => {
    this.context.setState({
      executeCommand: '',
      openAIStudy: true
    }, () => {
      this.context.setState(
        { executeCommand: chatText }
      );
    });
  };

  onAIInputChange = (chatText, triggerEvent = false) => {
    // 738

    if (!this.AIStudyToolIsInputDirty || triggerEvent) {
      this.AIStudyToolIsInputDirty = true;
      const customFields = {
        location_in_app: AI_STUDY_TOOL_EVENT.AI_STUDY_TOOL_LOCATION_IN_APP
      };
      CommonUtils.dispatchGaEvent(
        AUTH_HOME_CATEGORY,
        EVENT_AUTHHOMEEVENT,
        AI_STUDY_TOOL_EVENT.AI_STUDY_TOOL_CONVERSATION_EVENT_ACTION,
        null,
        customFields
      );
    }
  };

  onTocClick = () => {
    const { herobanner } = this.context.props;
    const { tableOfContents } = getSnapshot(herobanner);
    const isMLM = this.location === MLM_AUTH_HOME;
    const isTableOfContentsFetched = !tableOfContents?.isTOCFetchFailed
      && (tableOfContents?.data && !CommonUtils.isEmpty(tableOfContents.data));

    /**
     * Fetch asset for hero book on click of the TOC dropdown
     * If it is not home2 route or
     * If tableOfContents is not already fetched on load
     */
    const fetchAsset = !isMLM || !isTableOfContentsFetched;

    if (!this.isTocClicked) {
      this.handleTocClick(fetchAsset);
      this.isTocClicked = true;
    }
  };

  /**
   * Handle related channel card click
   *
   */
  onRelatedChannelClick = () => {
    const { herobanner } = this.context.props || {};
    const { channelId, popularChannels } = getSnapshot(herobanner);
    const channelCards = CommonUtils.getActiveChannelCards(popularChannels?.channelCards, true);
    const mostRelatedChannelIdx = channelCards.findIndex(card => card.channelId === channelId);

    this.context.coursePlugin.handlePopularChannelCardClick(mostRelatedChannelIdx, null, true);
  };

  /**
   * Return Empty state channel card list
   *
   */
  getPopularChannelList = channels => ({
    ...channels,
    subscriptionText: this.getSubscriptionText(),
    config: this.context.commonPlugin.getPopularChannelsConfig()
  });

  /**
   * If a MLM user click on the channel card, it will be redirect to the channel page
   * @param {*} index
   */

  onMLMPopularChannelCardClick = (index) => {
    const { herobanner } = this.context.props;
    let findChannelCard = null;
    if (index === 0 || index) {
      const popularChannelsData = getSnapshot(herobanner).popularChannels;
      const popularCards = CommonUtils.getActiveChannelCards(popularChannelsData.channelCards, false);
      findChannelCard = popularCards[index];
      if (findChannelCard) {
        const url = findChannelCard?.url;
        if (url && CommonUtils.isAbsoluteURL(url)) {
          window.open(CommonUtils.getUrlWithUTMParams(url, CHANNELS_CONTAINER_PARAM));
        } else {
          window.open(CommonUtils.getUrlWithUTMParams(`${env.STUDY_CHANNEL_URL}${url}`, CHANNELS_CONTAINER_PARAM));
        }
        const { flags } = this.context.state;
        const customFields = CommonUtils.appendStudentChannelCardEtextCustomFields({
          is_ghost_account: CommonUtils.isGhostAccount(),
          location_in_app: CHANNEL_CARD_EVENT.CHANNELS_CONTAINER_FIELD,
          channel_id: findChannelCard.channelId || null,
          container_value: CommonUtils.getCurrentScreenEventLabel(flags)
        });

        CommonUtils.dispatchGaEvent(
          AUTH_HOME_CATEGORY,
          EVENT_AUTHHOMEEVENT,
          CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_CLICK_EVENT_ACTION,
          CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_EVENT_LABEL,
          customFields
        );
      }
    }
    this.context.commonPlugin.handleChannelCardClick();
  };

  /**
   * User clicked on the Practice now cta
   */

  onPracticeCTAClick = () => {
    const { herobanner } = this.context.props;
    const channelData = herobanner.getChannelData(herobanner.channelId);
    if (channelData && CommonUtils.isAbsoluteURL(channelData.learnUrl)) {
      const practiceUrl = CommonUtils.getUrlWithUTMParams(channelData.learnUrl, CHANNEL_ASSET_PRATICE_QUERY_PARAM);
      if (this.context.commonPlugin.isIntegratedUI()) {
        this.context.globalNavPlugin.handleSetSelectedMenu(LEFT_NAV_MENU_KEY.STUDYPREP, practiceUrl);
      } else {
        window.open(practiceUrl);
      }
    }
    this.tiggerChannelCardElement(LOCATION_IN_APP.PRACTICE);
    this.context.commonPlugin.handleChannelCardClick();
  };


  /**
  * User clicked on the Practice now cta
  */

  onAssetTabTabClick = (activeTab) => {
    this.context.setState({ activeTocAssetFlow: activeTab });
  };

  /**
   *
   * function to handle launch diagnostics click
   * @returns
   */
  onLaunchDiagnosticsClick = () => {
    const { match, bookshelf, user } = this.context.props;
    const bookData = CommonUtils.getMLMRouteBookData(match) || {};
    const heroBook = this.getHeroBook();
    const courseRegistrarId = bookData?.courseId || null;
    const userData = getSnapshot(user) || {};
    bookshelf.fetchDiagnosticsInfo({ launchContextId: this.context.launchContextId, courseRegistrarId, userData });
  };

  /**
   * Method to get asset
   *
   */

  // eslint-disable-next-line class-methods-use-this
  getAssetTabs = () => {
    const { herobanner, language } = this.context.props;
    const { activeTocAssetFlow } = this.context.state;
    const count = herobanner.getPracticeAssetsCount();

    const tabList = [{
      id: ASSET_TABS_VALUE.LEARN,
      name: language.getMessage('hero.learnTxt')
    }];

    if (count > 0) {
      tabList.push({
        id: ASSET_TABS_VALUE.PRACTICE,
        name: language.getMessage('hero.practice'),
        count
      });
    }

    const assetTabs = {
      tabs: tabList,
      activeTab: activeTocAssetFlow
    };

    return assetTabs;
  };

  /**
   * Event for left menu click
   *
   */
  tiggerABTestingForGlobalNavEvent = (leftMenu = '', isInitialLoad = false) => {
    if (!leftMenu || !isInitialLoad) {
      return;
    }
    const { herobanner } = this.context.props;
    const { flags } = this.context.state;
    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      channels_sub_status: CommonUtils.channelsSubStatus(),
      channel_id: herobanner.channelId || null,
      container_value: CommonUtils.getCurrentScreenEventLabel(flags)
    };

    // Event 825
    CommonUtils.dispatchGaEvent(
      EREADER_CATEGORY,
      EVENT_EREADEREVENT,
      GLOBAL_NAV_EVENT.LEFT_NAV_ELEMENT_CLICK_EVENT_ACTION,
      GLOBAL_NAV_EVENT[leftMenu.toUpperCase()],
      customFields
    );
  };

  /**
   * Method to get Active Course Tab
   *
   */
  // eslint-disable-next-line class-methods-use-this
  getActiveCourseTab = () => HeroDashboardPlugin.activeCourseTab || {};

  getQrCodeCopy = () => {
    const {
      flags = {}
    } = this.context.state || {};
    const {
      language
    } = this.context?.props || {};
    const qrCodeVariant = flags?.qrCodeVariant;
    let details = language.getMessage('qrcode.details');

    switch (qrCodeVariant) {
      case QR_CODE_VARIANT.V1:
      case QR_CODE_VARIANT.V2:
        details = language.getMessage('qrcode.detailsv1');
        break;
      case QR_CODE_VARIANT.V3:
        details = language.getMessage('qrcode.detailsv2');
        break;
      default:
        break;
    }

    return {
      details
    };
  };

  getQrCodeData = () => {
    const {
      flags,
      modalProps
    } = this.context?.state || {};
    const {
      qrCode
    } = modalProps;
    const {
      language
    } = this.context?.props || {};
    const appstoreRating = this.context.getContainerData(
      'heroView',
      APPSTORE_RATING
    ) || null;
    const playstoreRating = this.context.getContainerData(
      'heroView',
      PLAYSTORE_RATING
    ) || null;
    const {
      details
    } = this.getQrCodeCopy();

    return {
      heading: language.getMessage('qrcode.title'),
      qrCode,
      details,
      variant: flags?.qrCodeVariant,
      appStoreData: [
        { icon: 'AppStore', text: appstoreRating ? `${appstoreRating} ★ ${language.getMessage('qrcode.appstore')}` : null },
        { icon: 'PlayStore', text: playstoreRating ? `${playstoreRating} ★ ${language.getMessage('qrcode.playstore')}` : null }
      ]
    };
  };

  showQrCodeTestVariants = () => {
    const {
      user
    } = this.context.props || {};
    const {
      isQrCodeBannerDismissed,
      flags,
      modalProps
    } = this.context.state || {};
    const {
      isQRCodeMsg
    } = modalProps || {};
    const {
      mobileAccess
    } = getSnapshot(user);

    const error = isQRCodeMsg;
    const isUS = this.context.nativeAppBannerPlugin.isCountryUs();
    const isDismissed = isQrCodeBannerDismissed
      && !CommonUtils.localStringCompare(flags?.qrCodeVariant, QR_CODE_VARIANT.V6);
    const enableQrCodeTestVariants = Boolean(flags?.qrCodeVariant);

    return enableQrCodeTestVariants
      && !isDismissed
      && isUS
      && !mobileAccess
      && !error;
  };

  handleQrCodeClose = () => {
    const payload = {
      qrCodeBannerDismissed: true
    };

    CommonUtils.updateUserPreference(payload);
    this.context.userPreferences.userActivity = { ...(this.context.userPreferences?.userActivity || {}) };
    this.context.userPreferences.userActivity.qrCodeBannerDismissed = true;

    this.context.setState({
      isQrCodeBannerDismissed: true
    });

    this.context.handleQRCodeClose(true);
  };

  renderQRCodeV3Container = () => {
    const {
      language
    } = this.context?.props || {};
    const {
      flags
    } = this.context?.state || {};
    const showFullContainerQrCode = CommonUtils.localStringCompare(flags?.qrCodeVariant, QR_CODE_VARIANT.V3);

    if (!this.showQrCodeTestVariants() || !showFullContainerQrCode) {
      return null;
    }

    return (
      <Container
        className="popular-channel-slider qr-container-v3"
        enableGutters
        enableTopAndBottomPadding
      >
        <Suspense fallback={<Fragment />}>
          <QrCodeContainer
            subHeading={language.getMessage('qrcode.subtitle')}
            {...this.getQrCodeData()}
            onQrCodeInit={this.onQrCodeInit}
            theme={THEMES.LIGHT}
          />
        </Suspense>
      </Container>
    );
  };

  onQrCodeInit = () => {
    const {
      flags
    } = this.context.state || {};

    const qrCodeEventLabel = QR_CODE_TEST_VARIANT_EVENT_LABEL[flags?.qrCodeVariant];

    if (qrCodeEventLabel) {
      CommonUtils.logABTestingEvent(qrCodeEventLabel);
    }
  };
}
